import { useHasScrollTop } from '@affine/component';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import clsx from 'clsx';
import {
  type CSSProperties,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import * as styles from './index.css';

interface SidebarContainerProps extends PropsWithChildren {
  className?: string;
  padding?: 'default' | 'none';
}

interface SidebarScrollableContainerProps extends SidebarContainerProps {
  contentPadding?: 'default' | 'none';
  nativeScrollSnap?: boolean;
  onScrollTopChange?: (scrollTop: number) => void;
  scrollSnap?: 'none' | 'proximity';
  scrollSnapPaddingTop?: number;
  scrollRestoreKey?: string | null;
  scrollbarVisibility?: 'always' | 'auto' | 'hover' | 'scroll';
  scrollTop?: number;
  snapScrollTop?: (scrollTop: number) => number;
}

export function SidebarContainer({
  children,
  className,
  padding = 'default',
}: SidebarContainerProps) {
  return (
    <div
      className={clsx([styles.baseContainer, className])}
      data-padding={padding}
    >
      {children}
    </div>
  );
}

export function SidebarScrollableContainer({
  children,
  className,
  contentPadding = 'default',
  nativeScrollSnap = false,
  onScrollTopChange,
  scrollSnap = 'none',
  scrollSnapPaddingTop = 0,
  scrollRestoreKey,
  scrollbarVisibility,
  scrollTop,
  snapScrollTop,
}: SidebarScrollableContainerProps) {
  const [setContainer, hasScrollTop] = useHasScrollTop();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const restoredScrollKeyRef = useRef<string | null>(null);
  const setViewport = useCallback(
    (element: HTMLDivElement | null) => {
      viewportRef.current = element;
      setContainer(element);
    },
    [setContainer]
  );
  const scrollSnapStyle =
    scrollSnapPaddingTop > 0
      ? ({
          '--sidebar-scroll-snap-padding-top': `${scrollSnapPaddingTop}px`,
        } as CSSProperties)
      : undefined;

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !scrollRestoreKey) return;
    if (restoredScrollKeyRef.current === scrollRestoreKey) return;

    restoredScrollKeyRef.current = scrollRestoreKey;
    const restoreScrollTop = () => {
      viewport.scrollTop = snapScrollTop?.(scrollTop ?? 0) ?? scrollTop ?? 0;
    };
    restoreScrollTop();
    const animationFrame = requestAnimationFrame(restoreScrollTop);
    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [scrollRestoreKey, scrollTop, snapScrollTop]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !onScrollTopChange) return;

    let timeout: ReturnType<typeof setTimeout> | null = null;
    let touchActive = false;
    let pendingScroll = false;
    const supportsScrollEnd = 'onscrollend' in viewport;
    const clearScrollTimeout = () => {
      if (timeout !== null) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
    const persistScrollTop = () => {
      pendingScroll = false;
      clearScrollTimeout();
      if (viewport.scrollTop <= 1) {
        if (viewport.scrollTop !== 0) {
          viewport.scrollTop = 0;
        }
        onScrollTopChange(0);
        return;
      }

      const currentScrollTop = viewport.scrollTop;
      const nextScrollTop =
        snapScrollTop?.(currentScrollTop) ?? currentScrollTop;
      if (Math.abs(nextScrollTop - currentScrollTop) > 0.5) {
        streamLainAnimationController.scrollTo(viewport, {
          top: nextScrollTop,
        });
      }
      onScrollTopChange(nextScrollTop);
    };
    const scheduleFallbackPersist = (delay = 260, force = false) => {
      if (supportsScrollEnd && !force) return;
      clearScrollTimeout();
      timeout = setTimeout(() => {
        const nextScrollTop =
          touchActive && scrollSnap !== 'none' ? null : viewport.scrollTop;
        timeout = null;
        if (nextScrollTop === null) {
          scheduleFallbackPersist(120);
          return;
        }
        persistScrollTop();
      }, delay);
    };
    const handleScroll = () => {
      pendingScroll = true;
      scheduleFallbackPersist();
    };
    const handleScrollEnd = () => {
      if (touchActive && scrollSnap !== 'none') return;
      persistScrollTop();
    };
    const handleTouchStart = () => {
      touchActive = true;
      clearScrollTimeout();
    };
    const handleTouchEnd = () => {
      touchActive = false;
      if (pendingScroll) {
        scheduleFallbackPersist(120, true);
      }
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    viewport.addEventListener('scrollend', handleScrollEnd);
    viewport.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    viewport.addEventListener('touchend', handleTouchEnd, { passive: true });
    viewport.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    return () => {
      clearScrollTimeout();
      viewport.removeEventListener('scroll', handleScroll);
      viewport.removeEventListener('scrollend', handleScrollEnd);
      viewport.removeEventListener('touchstart', handleTouchStart);
      viewport.removeEventListener('touchend', handleTouchEnd);
      viewport.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [onScrollTopChange, scrollSnap, snapScrollTop]);

  return (
    <ScrollArea.Root
      className={clsx([styles.scrollableContainerRoot, className])}
      data-content-padding={contentPadding}
      data-native-scroll-snap={nativeScrollSnap ? 'true' : undefined}
      data-scroll-snap={scrollSnap}
      style={scrollSnapStyle}
      type={scrollbarVisibility}
    >
      <div
        data-has-scroll-top={hasScrollTop}
        data-content-padding={contentPadding}
        className={styles.scrollTopBorder}
      />
      <ScrollArea.Viewport
        className={clsx([styles.scrollableViewport])}
        data-content-padding={contentPadding}
        data-native-scroll-snap={nativeScrollSnap ? 'true' : undefined}
        data-sidebar-scroll-viewport="true"
        data-scroll-snap={scrollSnap}
        ref={setViewport}
      >
        <div
          className={clsx([styles.scrollableContainer])}
          data-content-padding={contentPadding}
        >
          {children}
        </div>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        forceMount
        orientation="vertical"
        className={styles.scrollbar}
      >
        <ScrollArea.Thumb className={styles.scrollbarThumb} />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>
  );
}
